import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  addToWishlist,
  fetchWishlist,
  mergeWishlist,
  removeFromWishlist,
  type WishlistProduct,
} from '@/features/storefront/account/api/account';
import { CustomerAuthContext } from '@/features/storefront/account/customerAuthContext';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { pluginEnabled, themePlugin } from '@/features/storefront/plugins/registry';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';
import type { WishlistPlugin } from '@/features/storefront/themeSchema';
import { localList, useLocalList, type LocalList } from '@/features/storefront/wishlist/localList';

/**
 * The shopper's wishlist, wherever it lives.
 *
 * A guest's hearts are a list of product ids in this browser (`localList`). A
 * signed-in shopper's are on the server (`/storefront/account/wishlist`); the
 * moment they sign in, whatever they hearted as a guest is merged into the
 * account and the browser's copy is cleared. A theme preview never talks to a
 * server - it keeps the guest list whoever is "signed in" there.
 *
 * Taps are optimistic: the heart fills before the request lands, and an
 * override is kept per product until the server's answer replaces it. A
 * failure puts the heart back the way it was.
 */

/** As many as the server keeps (`MAX_WISHLIST_ITEMS`). */
export const WISHLIST_MAX = 200;
export const WISHLIST_STORAGE_KEY = 'neon_wishlist';

export function guestWishlist(sourceKey: string): LocalList {
  return localList(WISHLIST_STORAGE_KEY, sourceKey, WISHLIST_MAX);
}

// --- Optimistic overrides ---------------------------------------------------
type Overrides = ReadonlyMap<string, boolean>;
let overrides: Overrides = new Map();
const overrideListeners = new Set<() => void>();

function setOverride(productId: string, saved: boolean | null) {
  const next = new Map(overrides);
  if (saved === null) next.delete(productId);
  else next.set(productId, saved);
  overrides = next;
  overrideListeners.forEach((listener) => listener());
}

function subscribeOverrides(listener: () => void) {
  overrideListeners.add(listener);
  return () => overrideListeners.delete(listener);
}

const getOverrides = () => overrides;

// --- Merge on sign-in -------------------------------------------------------
let merging: Promise<void> | null = null;

function mergeGuestList(list: LocalList, client: QueryClient, key: readonly unknown[]) {
  if (merging) return merging;
  const pending = [...list.get()];
  if (pending.length === 0) return Promise.resolve();
  merging = mergeWishlist(pending)
    .then((saved) => {
      client.setQueryData(key, saved);
      // A first read that left before the merge landed must not win.
      void client.invalidateQueries({ queryKey: key });
      // Only what was sent: a heart tapped while the merge was in flight stays.
      list.set(list.get().filter((id) => !pending.includes(id)));
    })
    .catch(() => {
      /* kept in the browser; the next page load tries again */
    })
    .finally(() => {
      merging = null;
    });
  return merging;
}

/** Test seam: forget optimistic state between tests. */
export function resetWishlistState(): void {
  overrides = new Map();
  merging = null;
}

export interface WishlistState {
  /** Saved product ids, newest first. */
  ids: readonly string[];
  count: number;
  has(productId: string): boolean;
  /** Save or unsave; resolves once the server agrees, rejects (and reverts) if it does not. */
  toggle(productId: string): Promise<void>;
  /** True when the list is the account's rather than this browser's. */
  signedIn: boolean;
  /** The account's saved products as cards; undefined for a guest or while loading. */
  products: WishlistProduct[] | undefined;
  loading: boolean;
}

export function useWishlist(): WishlistState {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const auth = useContext(CustomerAuthContext);
  const client = useQueryClient();
  const signedIn = !source.preview && auth?.status === 'authenticated';
  const list = guestWishlist(source.key);
  const guestIds = useLocalList(list);
  const pending = useSyncExternalStore(subscribeOverrides, getOverrides, getOverrides);
  const key = useMemo(() => [...scope, 'wishlist'] as const, [scope]);

  const server = useQuery({ queryKey: key, queryFn: fetchWishlist, enabled: signedIn });

  useEffect(() => {
    if (signedIn && list.get().length > 0) void mergeGuestList(list, client, key);
  }, [signedIn, list, client, key]);

  const ids = useMemo(() => {
    if (!signedIn) return guestIds;
    const fromServer = (server.data ?? []).map((product) => product.id);
    const added = [...pending].filter(([, saved]) => saved).map(([id]) => id);
    const removed = new Set([...pending].filter(([, saved]) => !saved).map(([id]) => id));
    // A guest list still waiting to be merged counts already: the hearts a
    // shopper tapped before signing in do not blink out while it travels.
    return [...new Set([...added, ...fromServer, ...guestIds])].filter((id) => !removed.has(id));
  }, [signedIn, guestIds, server.data, pending]);

  const idSet = useMemo(() => new Set(ids), [ids]);

  const toggle = useCallback(
    async (productId: string) => {
      const saving = !idSet.has(productId);
      if (!signedIn) {
        const current = list.get();
        list.set(saving ? [productId, ...current] : current.filter((id) => id !== productId));
        return;
      }
      setOverride(productId, saving);
      if (!saving && list.get().includes(productId)) {
        list.set(list.get().filter((id) => id !== productId));
      }
      try {
        const saved = await (saving ? addToWishlist(productId) : removeFromWishlist(productId));
        client.setQueryData(key, saved);
      } finally {
        setOverride(productId, null);
      }
    },
    [idSet, signedIn, list, client, key],
  );

  const products = useMemo(
    () =>
      signedIn && server.data
        ? server.data.filter((product) => pending.get(product.id) !== false)
        : undefined,
    [signedIn, server.data, pending],
  );

  return {
    ids,
    count: ids.length,
    has: (productId) => idSet.has(productId),
    toggle,
    signedIn,
    products,
    loading: signedIn && server.isPending,
  };
}

/** The plugin's settings, and whether the shop has it on. */
export function useWishlistPlugin(): WishlistPlugin & { on: boolean; ready: boolean } {
  const theme = useStorefrontTheme();
  // No theme is drawn around the wishlist, so the family never changes the answer.
  return {
    ...themePlugin(theme, 'wishlist'),
    on: pluginEnabled(theme, 'wishlist', ''),
    // False only in the moment before the theme arrives, so a page can wait
    // rather than flash "not here".
    ready: theme !== undefined,
  };
}
